<sbnb-loading-spinner *ngIf="loading$ | async"></sbnb-loading-spinner>
<sbnb-alert-card *ngIf="readonly$ | async">
  <ng-container *ngIf="adjustmentUuid$ | async as adjustmentUuid">
    <ng-container *ngIf="adjustmentUuid">
      This transaction is part of &nbsp;
      <a class="link" [routerLink]="['/operations/statements/adjustments']" [queryParams]="{ adjustmentUuid }">
        reservation adjustment
      </a>
      &nbsp; and cannot be edited.
    </ng-container>
    <ng-container *ngIf="!adjustmentUuid">
      This transaction is included in a published statement and cannot be edited.
    </ng-container>
  </ng-container>
</sbnb-alert-card>

<ng-container *ngIf="transaction$ | async as transaction">
  <sbnb-alert-card *ngIf="transaction.recurring_transaction_id" class="flex-col">
    <span>
      This transaction was created from
      <a
        class="link"
        [routerLink]="['/operations/statements/transactions/', transaction.recurring_transaction_id, 'recurring']">
        a scheduled transaction
      </a>
      <ng-container *ngIf="transaction.reservation_code">
        for reservation
        <span class="font-mono inline-flex items-center gap-1">
          {{ transaction.reservation_code }}
          <sbnb-copy-to-clipboard [content]="transaction.reservation_code" [mini]="true"></sbnb-copy-to-clipboard>
        </span>
      </ng-container>
      .
    </span>
  </sbnb-alert-card>
</ng-container>

<ng-container *ngIf="propertiesMatchingCurrentProperty$ | async as matchedCurrencyProperties">
  <form [formGroup]="transactionForm" *ngIf="(loading$ | async) !== true">
    <section class="mt-8">
      <h4 class="mb-2">Transaction details</h4>
    </section>

    <!-- Transaction date and recurrence -->
    <section class="mt-4 flex flex-row gap-4">
      <div class="basis-1/4">
        <label class="block text-sm text-night" [ngClass]="{ 'opacity-50': date.disabled }" for="date">
          Date of transaction
        </label>
        <ng-container *ngIf="!date.disabled && !transactionForm.disabled; else disabledDate">
          <mat-form-field
            class="datepicker mat-form-field-no-errors mb-8 w-full nopadding nomargin"
            #dateTrigger="matMenuTrigger"
            [matMenuTriggerFor]="dateMenu">
            <input matInput class="cursor-pointer" value="{{ date.value | date: 'mediumDate' }}" />
          </mat-form-field>

          <mat-menu #dateMenu="matMenu" class="filters__cdk-menu">
            <sbnb-date-picker
              [numMonths]="1"
              [rangeSelect]="false"
              [checkInOutStyling]="false"
              [showDurationLabels]="false"
              (click)="$event.stopPropagation()"
              (datesSelected)="
                dateTrigger.closeMenu(); transactionForm.get('date')?.setValue($event)
              "></sbnb-date-picker>
          </mat-menu>
        </ng-container>
        <ng-template #disabledDate>
          <mat-form-field class="datepicker mat-form-field-no-errors mb-8 w-full nopadding nomargin">
            <input matInput value="{{ date.value | date: 'mediumDate' }}" [disabled]="true" />
          </mat-form-field>
        </ng-template>
      </div>
      <div class="basis-1/4" *ngIf="recurring || !editing">
        <label class="block text-sm text-gray-600" for="date" [ngClass]="{ 'opacity-50': recurrence.disabled }">
          Repeat
        </label>
        <mat-form-field [matTooltip]="recurrenceTooltip" class="nopadding nomargin">
          <mat-select formControlName="recurrence" placeholder="Never">
            <mat-option *ngFor="let option of recurrenceOptions" [value]="option.value">
              {{ option.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="basis-2/4" *ngIf="!editing && recurrence.value">
        <label class="block text-sm text-gray-600" for="date" [ngClass]="{ 'opacity-50': recurrence.disabled }">
          Properties
        </label>
        <!-- NOTE: the multi property recurring transactions do not need to of the same currency,
         since each property selection will create a new recurring transaction. However, it's nice if they do belong to the same business,
         so they can share the same categories so we are using the matchedCurrencyProperties list. -->
        <mat-form-field
          class="w-full nopadding nomargin"
          data-cy="dropdown"
          *ngIf="matchedCurrencyProperties.length > 0">
          <sbnb-dropdown
            [options]="matchedCurrencyProperties"
            [formControl]="recurringPropertyIdsCtrl"
            placeholder="Select properties..."
            [multiple]="true"></sbnb-dropdown>
        </mat-form-field>
        <mat-hint class="text-xs">
          A recurring transaction will be created for each of the selected properties.
        </mat-hint>
      </div>
    </section>
    <section
      class="mt-4"
      *ngIf="!recurrence.value && (!transactionForm.disabled || (previousAttachments$ | async)?.length > 0)">
      <h4 class="mb-4">Transaction receipt(s)</h4>

      <sbnb-file-upload
        [multiple]="true"
        [disabled]="transactionForm.disabled"
        [maxSize]="10485760"
        [fileTypes]="['png', 'jpeg', 'jpg', 'pdf', 'heic', 'heif']"
        (filesSelected)="filesSelected($event)"
        [files]="previousAttachments$ | async"
        (fileRemoved)="fileRemoved($event)"
        [progress]="(saving$ | async) ? 50 : 0"
        #fileUpload>
        <ng-template>
          Drag & drop your receipts or
          <span class="link-emphasized cursor-pointer" (click)="fileUpload.openFileSelector()">click to browse</span>
        </ng-template>
      </sbnb-file-upload>
    </section>

    <sbnb-alert-card *ngIf="recurring && editing" class="gap-3 max-w-3xl mt-4">
      <sbnb-icon icon="details"></sbnb-icon>
      <span>
        Editing this recurring transaction will update all unlocked transactions. To target only future transactions,
        <a class="link" routerLink="/operations/statements/transactions/add">create a new recurring transaction.</a>
      </span>
    </sbnb-alert-card>

    <!-- Transaction lines -->
    <section class="mt-4">
      <h4 class="mb-4" *ngIf="!recurrence.value">Transaction items</h4>
      <ng-container formArrayName="lines">
        <section
          *ngFor="let line of lines.controls; let i = index"
          class="flex flex-col 2xl:flex-row 2xl:gap-4 bg-smoke-lighter p-4 rounded mb-4 relative">
          <ng-container [formGroup]="line">
            <section class="flex flex-col lg:flex-row lg:gap-4">
              <div class="basis-3/12" *ngIf="!(recurrence.value && !editing)">
                <label
                  class="block text-sm text-night"
                  for="property"
                  [ngClass]="{ 'opacity-50': transactionForm.disabled }">
                  Property
                </label>
                <mat-form-field class="w-full nopadding nomargin" data-cy="dropdown">
                  <sbnb-dropdown
                    [defaultOpen]="!editing"
                    [options]="matchedCurrencyProperties"
                    formControlName="property_id"
                    [placeholder]="'Select a property...'"
                    [resetOption]="true"
                    [multiple]="false"></sbnb-dropdown>
                </mat-form-field>
              </div>

              <div class="flex-1">
                <label
                  class="block text-sm text-night"
                  for="description"
                  [ngClass]="{ 'opacity-50': transactionForm.disabled }">
                  Description
                </label>
                <mat-form-field class="mat-form-field-no-errors mat-form-field-bg-white w-full">
                  <input matInput placeholder="Description" formControlName="description" />
                </mat-form-field>
              </div>

              <div class="basis-3/12">
                <label
                  class="block text-sm text-night"
                  for="category"
                  [ngClass]="{ 'opacity-50': transactionForm.disabled }">
                  Category
                </label>
                <mat-form-field class="mat-form-field-no-errors mat-form-field-bg-white w-full">
                  <sbnb-dropdown
                    [options]="(transactionConfiguration$ | async)?.categories"
                    formControlName="category"
                    placeholder="Select a category..."
                    [groupHeaderSelectable]="true"></sbnb-dropdown>
                </mat-form-field>
              </div>
            </section>
            <section class="flex flex-col md:flex-row md:justify-between gap-4">
              <div>
                <label
                  class="block text-sm text-night"
                  for="amount"
                  [ngClass]="{ 'opacity-50': transactionForm.disabled }">
                  Amount
                </label>
                <mat-form-field class="currency mat-form-field-no-errors mat-form-field-bg-white w-full">
                  <span *ngIf="currentPropertyCurrency$ | async as currency" matPrefix class="text-night-lighter">
                    {{ currency | currencyCode }}
                  </span>
                  <input matInput type="number" placeholder="0" formControlName="amount" />
                </mat-form-field>
              </div>

              <ng-container *ngIf="transactionConfiguration$ | async as transactionConfiguration">
                <div *ngIf="transactionConfiguration.with_markup && (reimbursementSelected$ | async) !== true">
                  <label
                    class="block text-sm text-night"
                    [ngClass]="{ 'opacity-50': transactionForm.disabled || line.get('markup')?.disabled }"
                    for="markup">
                    Markup
                  </label>
                  <mat-form-field class="currency mat-form-field-no-errors mat-form-field-bg-white w-full">
                    <span *ngIf="currentPropertyCurrency$ | async as currency" matPrefix class="text-night-lighter">
                      {{ currency | currencyCode }}
                    </span>
                    <input matInput type="number" placeholder="0" formControlName="markup" />
                  </mat-form-field>
                </div>
              </ng-container>

              <div>
                <label
                  class="block text-sm font-bold text-night-darker"
                  for="amount"
                  [ngClass]="{ 'opacity-50': transactionForm.disabled }">
                  Total
                </label>
                <mat-form-field class="currency mat-form-field-no-errors mat-form-field-bg-white w-full">
                  <span *ngIf="currentPropertyCurrency$ | async as currency" matPrefix class="text-night-lighter">
                    {{ currency | currencyCode }}
                  </span>
                  <input
                    matInput
                    disabled
                    type="number"
                    placeholder="0"
                    [value]="(line.get('amount')?.value + line.get('markup')?.value).toFixed(2)" />
                </mat-form-field>
              </div>
            </section>
          </ng-container>
          <div
            *ngIf="transactionForm.get('lines')?.value.length > 1"
            matTooltip="Remove line"
            matTooltipPosition="before"
            class="absolute top-3 right-3 flex cursor-pointer gap-1 items-center clickable"
            (click)="removeLine(i)">
            <sbnb-icon icon="trash"></sbnb-icon>
            <span class="visible lg:hidden font-bold">Remove line</span>
          </div>
        </section>
      </ng-container>

      <div class="flex flex-col lg:flex-row content-center lg:items-center px-4 gap-4">
        <div class="flex-1" *ngIf="!recurrence.value && !transactionForm.disabled">
          <sbnb-button
            class="flex-1"
            size="mini"
            type="text"
            label="+ Line"
            (clicked)="addLine()"
            [matTooltip]="
              transactionForm.get('lines')?.invalid ? 'Complete the current line before adding a new one' : ''
            "
            [disabled]="
              transactionForm.get('lines')?.invalid || transactionForm.disabled || (saving$ | async)
            "></sbnb-button>
        </div>
        <!-- to keep the totals to the right -->
        <div class="flex-1" *ngIf="recurrence.value"></div>

        <h4 class="px-4 text-lg text-right lg:text-left">
          <span class="visible lg:hidden">Amounts:</span>
          {{ currentPropertyCurrency$ | async | currencyCode }}
          {{ transactionForm.get('lines')?.value | transactionTotal: 'amount' }}
        </h4>
        <h4 class="px-4 text-lg text-right lg:text-left">
          <span class="visible lg:hidden">Markups:</span>
          {{ currentPropertyCurrency$ | async | currencyCode }}
          {{ transactionForm.get('lines')?.value | transactionTotal: 'markup' }}
        </h4>
        <h3 class="px-4 text-right lg:text-left">
          <span class="visible lg:hidden">Total:</span>
          {{ currentPropertyCurrency$ | async | currencyCode }}
          {{ transactionForm.get('lines')?.value | transactionTotal }}
        </h3>
        <div *ngIf="transactionForm.get('lines')?.value.length > 1" class="w-8">&nbsp;</div>
      </div>
    </section>

    <section class="mt-8 flex justify-end gap-4">
      <ng-template *ngTemplateOutlet="footer"></ng-template>
    </section>
  </form>
</ng-container>

<ng-template #footer>
  <sbnb-button
    icon="trash"
    type="secondary"
    *ngIf="editing && (adjustmentUuid$ | async) === null"
    label="Delete"
    [matTooltip]="recurring ? 'Delete all future scheduled transactions' : 'Delete transaction'"
    (clicked)="deleteTransaction()"></sbnb-button>
  <sbnb-button
    [loading]="saving$ | async"
    style="display: block"
    [label]="editing ? 'Update' : 'Create'"
    [matTooltip]="transactionForm.disabled ? 'This transaction cannot be edited.' : ''"
    [disabled]="
      transactionForm.invalid ||
      transactionForm.disabled ||
      (recurrence.value && recurringPropertyIdsCtrl.invalid) ||
      (saving$ | async)
    "
    (clicked)="saveChanges()"></sbnb-button>
</ng-template>
